/**
 * What a case is worth, line by line. Section 11 of the review of 2 October 2026.
 *
 * Each tooth is charged at its own item's price: three Zircon crowns and two
 * E.max veneers on one tray are five teeth at two prices, not five of the
 * headline item. An item sold per SPAN (a Maryland bridge, a full arch) is
 * charged once for the case, however many teeth it covers. Units beyond the
 * teeth marked are charged at the headline item's price, or not at all when
 * that is a span item; a case with no teeth marked is the headline item times
 * its units, or once for a span.
 *
 * The server's CasePricing (Dentist.Domain/Ceramica/CasePricing.cs) is the same
 * rule; the two must agree, or the mock shows figures the API never will.
 */

/** One priced item: the headline item of a case, or the item on a tooth. */
export interface PricedItem {
  itemId: string;
  product: string;
  unitPrice: number;
  span: boolean;
}

export interface PricedCase {
  headline: PricedItem;
  /** The item on each tooth, in the order the teeth were marked. */
  teeth: readonly PricedItem[];
  units: number;
}

export interface CaseLine {
  product: string;
  itemId: string;
  units: number;
  unitPrice: number;
  value: number;
}

export function caseLines(priced: PricedCase): CaseLine[] {
  const lines: CaseLine[] = [];
  const groups = new Map<string, { item: PricedItem; count: number }>();

  for (const tooth of priced.teeth) {
    const key = `${tooth.itemId}\u0000${tooth.product}`;
    const group = groups.get(key);
    if (group) group.count += 1;
    else groups.set(key, { item: tooth, count: 1 });
  }

  for (const { item, count } of groups.values()) {
    lines.push({
      product: item.product,
      itemId: item.itemId,
      units: count,
      unitPrice: item.unitPrice,
      value: item.span ? item.unitPrice : item.unitPrice * count,
    });
  }

  const { headline } = priced;
  const extra = priced.units - priced.teeth.length;
  if (extra > 0) {
    const whole = priced.teeth.length === 0 ? headline.unitPrice : 0;
    lines.push({
      product: headline.product,
      itemId: headline.itemId,
      units: extra,
      unitPrice: headline.unitPrice,
      value: headline.span ? whole : headline.unitPrice * extra,
    });
  }

  return lines;
}

/** The value of several cases, to the cent. */
export function caseValue(cases: readonly PricedCase[]): number {
  const total = cases.flatMap(caseLines).reduce((sum, line) => sum + line.value, 0);
  return Math.round(total * 100) / 100;
}

/**
 * A case's receipt line: what `caseLines` says, plus what Accounts reads and
 * changes on it. Request of 5 October 2026. The key is the server's
 * (CaseReceipt.cs): `{kitId}|teeth|{itemId}|{product}` for a line of teeth, and
 * `{kitId}|extra` for the units beyond them.
 */
export interface ReceiptLineDraft extends CaseLine {
  key: string;
  teeth: string[];
  perSpan: boolean;
}

export function receiptLines(
  kitId: string,
  priced: PricedCase,
  teeth: readonly string[],
): ReceiptLineDraft[] {
  const groups = new Map<string, { item: PricedItem; teeth: string[] }>();
  priced.teeth.forEach((item, index) => {
    const key = `${kitId}|teeth|${item.itemId}|${item.product}`;
    const group = groups.get(key);
    const tooth = teeth[index] ?? '';
    if (group) group.teeth.push(tooth);
    else groups.set(key, { item, teeth: [tooth] });
  });

  const lines: ReceiptLineDraft[] = [...groups.entries()].map(([key, { item, teeth: covered }]) => ({
    key,
    product: item.product,
    itemId: item.itemId,
    teeth: covered,
    units: covered.length,
    unitPrice: item.unitPrice,
    perSpan: item.span,
    value: item.span ? item.unitPrice : item.unitPrice * covered.length,
  }));

  const { headline } = priced;
  const extra = priced.units - priced.teeth.length;
  if (extra > 0) {
    const whole = priced.teeth.length === 0 ? headline.unitPrice : 0;
    lines.push({
      key: `${kitId}|extra`,
      product: headline.product,
      itemId: headline.itemId,
      teeth: [],
      units: extra,
      unitPrice: headline.unitPrice,
      perSpan: headline.span,
      value: headline.span ? whole : headline.unitPrice * extra,
    });
  }
  return lines;
}

/**
 * What a receipt line comes to at a new price, for showing Accounts the total
 * as they type, before the server has it. A line's value is its price times one
 * (charged once for the span), times its units, or nothing (a span already
 * charged on its teeth); which, is read from the value it has now. Request of
 * 3 October 2026.
 */
export function repricedValue(
  line: { unitPrice: number; units: number; perSpan: boolean; value: number },
  unitPrice: number,
): number {
  const times =
    line.unitPrice > 0 ? line.value / line.unitPrice : line.perSpan ? 1 : line.units;
  return Math.round(unitPrice * times * 100) / 100;
}
